import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useScript } from '@prophecy/utils/useScript';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import useInkeepSettings from './useInkeepSettings';

export function AiChatButton({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const inkeepSettings = useInkeepSettings();
  const [addCloseTrigger, setAddCloseTrigger] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inKeepWidgetRef = useRef<any>();
  const inKeepPortalRef = useRef<ShadowRoot>();
  const modalOverlayRef = useRef<HTMLDivElement>();

  const openModal = usePersistentCallback(() => {
    inKeepWidgetRef.current?.update({ modalSettings: { isOpen: true } });
    queueMicrotask(() => {
      modalOverlayRef.current = inKeepPortalRef.current?.querySelector('div.ikp-modal__overlay') as HTMLDivElement;
      setAddCloseTrigger(true);
    });
  });

  const closeModal = usePersistentCallback(() => {
    setAddCloseTrigger(false);
    inKeepWidgetRef.current?.update({ modalSettings: { isOpen: false } });
    modalOverlayRef.current?.removeEventListener('click', closeModal);
    modalOverlayRef.current = undefined;
    onClose();
  });

  useEffect(() => {
    if (isOpen) {
      openModal();
    }
  }, [isOpen, openModal]);

  useScript({
    src: 'https://cdn.jsdelivr.net/npm/@inkeep/cxkit-js@0.5/dist/embed.js',
    defer: true,
    onLoad: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).Inkeep.initInkeep();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inKeepWidgetRef.current = (window as any).Inkeep.ModalSearchAndChat(inkeepSettings);
      // poll for the inkeep-portal element
      const interval = setInterval(() => {
        const inKeep = document.querySelector('inkeep-portal') as HTMLElement;
        if (inKeep) {
          inKeepPortalRef.current = (inKeep.children[0] as HTMLElement).shadowRoot as ShadowRoot;
          clearInterval(interval);
        }
      }, 100);
    }
  });

  // inline style because this is rendered inside a shadow dom, styled components don't work
  return (
    addCloseTrigger &&
    modalOverlayRef.current &&
    createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}
        onClick={closeModal}
      />,
      modalOverlayRef.current
    )
  );
}
